/* eslint sort-keys: ["error"] */
import type { NodeSpec } from './NodeSpec';
import type { BaseNodeCfg, NodeCfg, NodeKind } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { activeOperatorUiNodeSpec } from './active-operator.ui-spec';
import { startUiNodeSpec } from './start.ui-spec';
import { appletEventEmitUiNodeSpec } from './applet-event-emit.ui-spec';
import { appletEventListenNodeSpec } from './applet-event-listen.ui-spec';
import { comparisonStringUiNodeSpec } from './comparison-string.ui-spec';
import { materialLookupUiNodeSpec } from './material-lookup.ui-spec';
import { eventSwitchUiNodeSpec } from './event-switch.ui-spec';
import { varReadNodeSpec } from './var-read.ui-spec';
import { varWriteNodeSpec } from './var-write.ui-spec';
import { varWriteMultipleNodeSpec } from './var-write-multiple.ui-spec';
import { varAssignedNodeSpec } from './var-assigned.ui-spec';
import { txtFormatNodeSpec } from './txt-format.ui-spec';
import { boolCastNodeSpec } from './bool-cast.ui-spec';
import { numCastNodeSpec } from './num-cast.ui-spec';
import { strCastNodeSpec } from './str-cast.ui-spec';
import { booleanNodeSpec } from './boolean.ui-spec';
import { numberNodeSpec } from './number.ui-spec';
import { textNodeSpec } from './text.ui-spec';
import { jsonAggNodeSpec } from './json-agg.ui-spec';
import { jsonParseNodeSpec } from './json-parse.ui-spec';
import { timerNodeSpec } from './timer.ui-spec';
import { activatePickLightNodeSpec } from './activate-pick-light.ui-spec';
import { scriptUiNodeSpec } from './script.ui-spec';
import { addMaterialNoteUiNodeSpec } from './add-material-note.ui-spec';
import { createMaterialUiNodeSpec } from './create-material.ui-spec';
import { importMaterialUiNodeSpec } from './import-material.ui-spec';
import { openProtocolConnectionNodeSpec } from './open-protocol-connection.ui-spec';
import { tcpConnectionNodeSpec } from './tcp-connection.ui-spec';
import { udpConnectionNodeSpec } from './udp-connection.ui-spec';
import { mqttPublishNodeSpec } from './mqtt-publish.ui-spec';
import { mqttSubscribeNodeSpec } from './mqtt-subscribe.ui-spec';
import { sparkplugCommanderNodeSpec } from './sparkplug-commander.ui-spec';
import { sparkplugConsumerNodeSpec } from './sparkplug-consumer.ui-spec';
import { torqueToolCommanderNodeSpec } from './torque-tool-commander.ui-spec';
import { torqueToolConsumerNodeSpec } from './torque-tool-consumer.ui-spec';
import { isduReadNodeSpec } from './isdu-read.ui-spec';
import { isduWriteNodeSpec } from './isdu-write.ui-spec';
import { booleanComparisonUiNodeSpec } from './boolean-comparison.ui-spec';
import { comparisonNumberUiNodeSpec } from './comparison-number.ui-spec';
import { defaultValUiNodeSpec } from './default-val.ui-spec';
import { numClampUiNodeSpec } from './num-clamp.ui-spec';
import { sliceUiNodeSpec } from './slice.ui-spec';
import { notUiNodeSpec } from './not.ui-spec';
import { timestampUiNodeSpec } from './timestamp.ui-spec';
import { eventDelayUiNodeSpec } from './event-delay.ui-spec';
import { eventThrottleUiNodeSpec } from './event-throttle.ui-spec';
import { eventOneShotUiNodeSpec } from './event-one-shot.ui-spec';
import { nextTabUiNodeSpec } from './next-tab.ui-spec';
import { goToTabUiNodeSpec } from './go-to-tab.ui-spec';
import { previousTabUiNodeSpec } from './previous-tab.ui-spec';
import { getActiveTabUiNodeSpec } from './get-active-tab.ui-spec';
import { materialComparisonUiNodeSpec } from './material-comparison.ui-spec';
import { materialSerialNumberUiNodeSpec } from './material-serial-number.ui-spec';
import { attributeMaterialLinkUiNodeSpec } from './attribute-material-link.ui-spec';
import { materialAssemblyUiNodeSpec } from './material-assembly.ui-spec';
import { materialDisassemblyUiNodeSpec } from './material-disassembly.ui-spec';
import { updateMaterialStatusUiNodeSpec } from './update-material-status.ui-spec';
import { fileReadUiNodeSpec } from './file-read.ui-spec';
import { fileWriteUiNodeSpec } from './file-write.ui-spec';
import { flowErrorUiNodeSpec } from './flow-error.ui-spec';
import { workOrderUiNodeSpec } from './work-order.ui-spec';
import { workOrderSetStatusUiNodeSpec } from './work-order-set-status.ui-spec';
import { processStatusUiNodeSpec } from './process-status.ui-spec';
import { workInstructionStatusUiNodeSpec } from './work-instruction-status.ui-spec';
import { deactivatePickLightUiNodeSpec } from './deactivate-pick-light.ui-spec';
import { processSolveUiNodeSpec } from './process-solve.ui-spec';
import { pickLightChannelUiNodeSpec } from './pick-light-channel.ui-spec';
import { performanceDataCollectUiNodeSpec } from './performance-data-collect.ui-spec';
import { functionalSelectUiNodeSpec } from './functional-select.ui-spec';
import { functionalInputsUiNodeSpec } from './functional-inputs.ui-spec';
import { functionalOutputsUiNodeSpec } from './functional-outputs.ui-spec';
import { ioLinkUiNodeSpec } from './io-link.ui-spec';
import { processCollectUiNodeSpec } from './process-collect.ui-spec';
import { workInstructionUiNodeSpec } from './work-instruction.ui-spec';
import { resetVariablesSpec } from './reset-variables.ui-spec';
import { logOutOperatorUiNodeSpec } from './log-out-operator.ui-spec';

export * from './NodeSpec';

/**
 * a specification for how to initialize and work with a node, keyed by the node kind
 */
export const narrowNodeKindMap: {
    [kind in NodeKind]: NodeSpec<NodeCfg<kind>>;
} = {
    activatePickLight: activatePickLightNodeSpec,
    activeOperator: activeOperatorUiNodeSpec,
    addMaterialNote: addMaterialNoteUiNodeSpec,
    appletEventEmit: appletEventEmitUiNodeSpec,
    appletEventListen: appletEventListenNodeSpec,
    attributeMaterialLink: attributeMaterialLinkUiNodeSpec,
    boolCast: boolCastNodeSpec,
    boolean: booleanNodeSpec,
    booleanComparison: booleanComparisonUiNodeSpec,
    comparisonNumber: comparisonNumberUiNodeSpec,
    comparisonString: comparisonStringUiNodeSpec,
    createMaterial: createMaterialUiNodeSpec,
    deactivatePickLight: deactivatePickLightUiNodeSpec,
    defaultVal: defaultValUiNodeSpec,
    eventDelay: eventDelayUiNodeSpec,
    eventOneShot: eventOneShotUiNodeSpec,
    eventSwitch: eventSwitchUiNodeSpec,
    eventThrottle: eventThrottleUiNodeSpec,
    fileRead: fileReadUiNodeSpec,
    fileWrite: fileWriteUiNodeSpec,
    flowError: flowErrorUiNodeSpec,
    functionalInputs: functionalInputsUiNodeSpec,
    functionalOutputs: functionalOutputsUiNodeSpec,
    functionalSelect: functionalSelectUiNodeSpec,
    getActiveTab: getActiveTabUiNodeSpec,
    goToTab: goToTabUiNodeSpec,
    importMaterial: importMaterialUiNodeSpec,
    ioLink: ioLinkUiNodeSpec,
    isduRead: isduReadNodeSpec,
    isduWrite: isduWriteNodeSpec,
    jsonAgg: jsonAggNodeSpec,
    jsonParse: jsonParseNodeSpec,
    logOutOperator: logOutOperatorUiNodeSpec,
    materialAssembly: materialAssemblyUiNodeSpec,
    materialComparison: materialComparisonUiNodeSpec,
    materialDisassembly: materialDisassemblyUiNodeSpec,
    materialLookup: materialLookupUiNodeSpec,
    materialSerialNumber: materialSerialNumberUiNodeSpec,
    mqttPublish: mqttPublishNodeSpec,
    mqttSubscribe: mqttSubscribeNodeSpec,
    nextTab: nextTabUiNodeSpec,
    not: notUiNodeSpec,
    numCast: numCastNodeSpec,
    numClamp: numClampUiNodeSpec,
    number: numberNodeSpec,
    openProtocolConnection: openProtocolConnectionNodeSpec,
    performanceDataCollect: performanceDataCollectUiNodeSpec,
    pickLightChannel: pickLightChannelUiNodeSpec,
    previousTab: previousTabUiNodeSpec,
    processCollect: processCollectUiNodeSpec,
    processSolve: processSolveUiNodeSpec,
    processStatus: processStatusUiNodeSpec,
    resetVariables: resetVariablesSpec,
    script: scriptUiNodeSpec,
    slice: sliceUiNodeSpec,
    sparkplugCommander: sparkplugCommanderNodeSpec,
    sparkplugConsumer: sparkplugConsumerNodeSpec,
    start: startUiNodeSpec,
    strCast: strCastNodeSpec,
    tcpConnection: tcpConnectionNodeSpec,
    text: textNodeSpec,
    timer: timerNodeSpec,
    timestamp: timestampUiNodeSpec,
    torqueToolCommander: torqueToolCommanderNodeSpec,
    torqueToolConsumer: torqueToolConsumerNodeSpec,
    txtFormat: txtFormatNodeSpec,
    udpConnection: udpConnectionNodeSpec,
    updateMaterialStatus: updateMaterialStatusUiNodeSpec,
    varAssigned: varAssignedNodeSpec,
    varRead: varReadNodeSpec,
    varWrite: varWriteNodeSpec,
    varWriteMultiple: varWriteMultipleNodeSpec,
    workInstruction: workInstructionUiNodeSpec,
    workInstructionStatus: workInstructionStatusUiNodeSpec,
    workOrder: workOrderUiNodeSpec,
    workOrderSetStatus: workOrderSetStatusUiNodeSpec,
};

export const nodeKindMap = narrowNodeKindMap as unknown as { // widening for performance
    [K in NodeKind]: NodeSpec<BaseNodeCfg>;
};

export const NODE_MIN_WIDTH_PX = 180;
